"use client"

import DoktorIcon from "@/layout/DoktorIcons"
import { cn } from "@/utils/utils"
import { useEffect, useRef, useState } from "react"
import { FiCheck, FiChevronDown, FiX } from "react-icons/fi"

/**
 * A searchable dropdown, single- or multi-select, that can show each option's icon.
 *
 * Replaces the native `<select>` for lists long enough that scrolling is worse than typing — 113
 * icons being the case that prompted it. The mode is set in code, not inferred from the value, so a
 * caller cannot accidentally get multi behaviour by passing an array.
 *
 * `showIcons` is opt-in for a reason: every icon is its own lazily-imported chunk, so a list of 113
 * would fire 113 requests. That is why the options only render once the panel is OPEN, and why the
 * same tradeoff is already made in `IconPicker` — closed, this costs nothing.
 *
 * Keyboard behaviour is implemented rather than assumed: a custom listbox that only responds to the
 * mouse would be a step down from the native control it replaces.
 *
 * `searchable` is on by default and worth turning off for short lists: a search box above five
 * options is a control nobody needs and one more thing to tab past.
 */

export type SearchableOption = {
  value: string
  label: string
  /** `icons` datasource value, resolved through DoktorIcon. Only used when `showIcons` is set. */
  icon?: string
  /**
   * Image URL shown as a thumbnail beside the label — for picking artwork, where the filename says
   * nothing about what will render. Takes precedence over `icon` when both are set.
   */
  thumbnail?: string
}

type CommonProps = {
  options: SearchableOption[]
  /** Off for short lists — the filter box is only worth its space when scrolling is worse. */
  searchable?: boolean
  /** Shown on the closed button when nothing is selected. */
  placeholder?: string
  disabled?: boolean
  loading?: boolean
  showIcons?: boolean
  className?: string
  classNameButton?: string
}

/** The "none" row, when a caller opts into one. Single-select only — see `allOptions`. */
const EMPTY_VALUE = ""

type SearchableSelectProps = CommonProps &
  /**
   * `emptyOption` adds a row that clears the value, labelled in the caller's own words
   * ("Ingen kampanjbild"). The X on the closed button does the same thing, but only once
   * something is set, and it reads as an affordance rather than a choice — so an explicitly
   * empty value is not otherwise reachable from the open list.
   *
   * Single-select only: in multi mode, deselecting every option already expresses it.
   */
  (| { mode?: "single"; value: string; onChange: (value: string) => void; emptyOption?: string }
    | { mode: "multi"; value: string[]; onChange: (value: string[]) => void }
  )

/**
 * The picture beside an option: a thumbnail when the option carries one, otherwise the icon.
 *
 * Rendered only while the panel is open — every DoktorIcon is a lazily-imported chunk, so a list of
 * 113 would fire 113 requests if they mounted eagerly.
 */
const OptionMedia = ({ option, showIcons }: { option?: SearchableOption; showIcons: boolean }) => {
  if (!option) return null
  if (option.thumbnail) {
    return (
      // Storyblok assets are SVG or PNG of unknown size, so the box is fixed and the image fits
      // inside it rather than dictating the row height.
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={option.thumbnail}
        alt=""
        aria-hidden
        className="h-6 w-10 shrink-0 rounded border border-neutral-20 bg-white object-contain p-0.5"
      />
    )
  }
  if (!showIcons) return null
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center">
      {option.icon || option.value ? (
        <DoktorIcon iconName={option.icon ?? option.value} width={18} height={18} aria-hidden />
      ) : null}
    </span>
  )
}

export const SearchableSelect = ({
  options,
  placeholder = "— välj —",
  disabled = false,
  loading = false,
  searchable = true,
  showIcons = false,
  className,
  classNameButton,
  ...selection
}: SearchableSelectProps) => {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState("")
  /** Which option the keyboard is on. Reset whenever the visible set changes. */
  const [active, setActive] = useState(0)

  const root = useRef<HTMLDivElement>(null)
  const search = useRef<HTMLInputElement>(null)

  const isMulti = selection.mode === "multi"
  const selected = isMulti ? selection.value : selection.value ? [selection.value] : []

  // Prepended rather than handled as a special case at render, so it takes part in filtering,
  // arrow-key movement and Enter like any other row.
  const allOptions =
    !isMulti && selection.emptyOption ? [{ value: EMPTY_VALUE, label: selection.emptyOption }, ...options] : options

  const needle = filter.trim().toLowerCase()
  const shown = needle
    ? allOptions.filter(option => `${option.label} ${option.value}`.toLowerCase().includes(needle))
    : allOptions

  // Closing on an outside click is what makes this feel like a control rather than a panel that
  // sticks. Bound only while open, so there is no listener on every mounted select.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onPointerDown)
    return () => document.removeEventListener("mousedown", onPointerDown)
  }, [open])

  useEffect(() => {
    if (open && searchable) search.current?.focus()
    else if (!open) setFilter("")
  }, [open, searchable])

  // A stale index would highlight the wrong row — or nothing — after typing.
  useEffect(() => setActive(0), [needle])

  const commit = (value: string) => {
    if (selection.mode === "multi") {
      const next = selection.value.includes(value)
        ? selection.value.filter(current => current !== value)
        : // Sorted, so the same set always produces identical story content.
          [...selection.value, value].sort()
      selection.onChange(next)
      return
    }
    selection.onChange(value)
    setOpen(false)
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      setOpen(false)
      return
    }

    // Closed, the keys that open a native <select> open this one. Without this, keydown runs before
    // the button's synthetic click and Enter would commit whichever row the cursor sat on — a
    // keyboard user would select the first option just by trying to open the list. preventDefault
    // stops that click from arriving and toggling the panel straight back shut.
    if (!open) {
      if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault()
        setOpen(true)
      }
      return
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      if (!shown.length) return
      const step = event.key === "ArrowDown" ? 1 : -1
      setActive(current => (current + step + shown.length) % shown.length)
      return
    }
    if (event.key === "Enter") {
      event.preventDefault()
      const option = shown[active]
      if (option) commit(option.value)
    }
  }

  const summary = () => {
    if (isMulti) {
      if (!selected.length) return placeholder
      if (selected.length === 1) return options.find(option => option.value === selected[0])?.label ?? selected[0]
      return `${selected.length} valda`
    }
    if (!selected.length) return placeholder
    // A value missing from the options still shows — it may be valid and simply unlisted.
    return options.find(option => option.value === selected[0])?.label ?? selected[0]
  }

  const hasSelection = selected.length > 0

  return (
    <div ref={root} className={cn("relative", className)}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
        onKeyDown={onKeyDown}
        className={cn(
          "flex w-full items-center gap-2 rounded-lg border border-neutral-20 bg-white px-3 py-2 text-left text-sm",
          "text-on-surface transition-colors hover:border-primary-50 disabled:bg-neutral-10",
          open && "border-primary-50",
          classNameButton
        )}
      >
        {!isMulti && hasSelection && (
          <OptionMedia option={options.find(o => o.value === selected[0])} showIcons={showIcons} />
        )}
        <span className={cn("min-w-0 grow truncate", !hasSelection && "text-neutral-50")}>{summary()}</span>
        {hasSelection && !disabled && (
          <FiX
            role="button"
            aria-label="Rensa"
            className="h-4 w-4 shrink-0 text-neutral-50 hover:text-primary-50"
            onClick={event => {
              // Without this the click reaches the button and reopens what it just cleared.
              event.stopPropagation()
              if (selection.mode === "multi") selection.onChange([])
              else selection.onChange("")
            }}
          />
        )}
        <FiChevronDown className={cn("h-4 w-4 shrink-0 text-neutral-50 transition-transform", open && "rotate-180")} />
      </button>

      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-neutral-20 bg-white shadow-elevation-1">
          {searchable && (
            <input
              ref={search}
              value={filter}
              onChange={event => setFilter(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Sök…"
              className="w-full rounded-t-lg border-b border-neutral-20 bg-white px-3 py-2 text-sm outline-none"
            />
          )}

          {loading && <p className="px-3 py-2 text-xs text-neutral-50">Hämtar…</p>}

          <ul role="listbox" aria-multiselectable={isMulti} className="max-h-56 overflow-y-auto py-1">
            {shown.map((option, index) => {
              // Compared against the raw value, not `selected`: an empty value is absent from that
              // list, so the "none" row would never show as chosen.
              const isSelected = isMulti ? selected.includes(option.value) : selection.value === option.value
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => commit(option.value)}
                    className={cn(
                      "flex w-full cursor-pointer items-center gap-2 px-3 py-1.5 text-left text-sm",
                      index === active && "bg-primary-05",
                      isSelected && "font-semibold text-primary-50"
                    )}
                  >
                    <OptionMedia option={option} showIcons={showIcons} />
                    <span className="min-w-0 grow truncate">{option.label}</span>
                    {isSelected && <FiCheck className="h-4 w-4 shrink-0" />}
                  </button>
                </li>
              )
            })}
          </ul>

          {!loading && shown.length === 0 && <p className="px-3 py-2 text-xs text-neutral-50">Inget matchar.</p>}
        </div>
      )}
    </div>
  )
}
